'use client';
import React from 'react';
import Link from 'next/link';

// Published on /contact already. Kept here so the footer and the contact page
// cannot drift, and so a visitor can reach us from any page rather than only
// after navigating to one.
const CONTACT_EMAIL = 'hello@techsonar.io';

const footerLinks = [
  {
    heading: 'Services',
    links: [
      { label: 'AppSec & Vulnerability Management', href: '/services/appsec' },
      { label: 'Compliance & Continuous Compliance', href: '/services/compliance' },
      { label: 'Threat Modelling & Architecture', href: '/services/threat-modelling' },
      { label: 'Continuous DevSecOps', href: '/services/devsecops' },
      { label: 'IaC & Pipeline Security', href: '/services/iac-pipeline' },
      { label: 'Cloud & Platform Engineering', href: '/services/cloud-platform' },
      { label: 'Penetration Testing', href: '/services/pentesting' },
      { label: 'AI Penetration Testing', href: '/services/ai-pentesting' },
    ],
  },
  {
    heading: 'Programmes',
    links: [
      // /programmes is in the sitemap but nothing linked to it, so Google had
      // no path to the page that lists all four.
      { label: 'All Programmes', href: '/programmes' },
      { label: 'Accelerator Programme', href: '/services/accelerator' },
      { label: 'Bespoke Security Programmes', href: '/services/security-programmes' },
      { label: 'Security Focus Programme', href: '/services/security-focus' },
      { label: 'IaC Security Programme', href: '/services/iac-programme' },
    ],
  },
  {
    heading: 'Industries',
    links: [
      { label: 'Financial Services', href: '/industries/financial-services' },
      { label: 'HealthTech & MedTech', href: '/industries/healthtech' },
      { label: 'SaaS & Technology', href: '/industries/saas-tech' },
      { label: 'Critical Infrastructure', href: '/industries/critical-infrastructure' },
      { label: 'Telecom', href: '/industries/telecom' },
      { label: 'FinTech & Payments', href: '/industries/fintech' },
      { label: 'AI & Data', href: '/industries/ai-data' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About TechSonar', href: '/about' },
      { label: 'How We Work', href: '/about#how-we-work' },
      { label: 'Careers', href: '/about#careers' },
      { label: 'Partners', href: '/partners' },
      { label: 'Contact', href: '/contact' },
    ],
  },
];

const complianceBadges = [
  { label: 'SOC 2', desc: 'Type II Certified' },
  { label: 'ISO 27001', desc: 'Certified' },
  { label: 'GDPR', desc: 'Compliant' },
];

export default function Footer() {
  return (
    <footer className="bg-[#050a0f] border-t border-[#222]">
      {/* Top section */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Logo + Newsletter */}
          <div className="lg:w-1/5 flex flex-col gap-8">
            <div>
              <span className="font-heading text-[#f5f5f8] text-xl font-medium tracking-wider uppercase">
                TECHSONAR
              </span>
              <p className="mt-3 text-sm text-[#666] leading-relaxed">
                Specialist DevSecOps consultancy. Embedded, continuous, senior-led security for engineering teams.
              </p>
            </div>

            {/* There was a newsletter signup here. Its onSubmit was
                `e.preventDefault()` and nothing else: no handler, no endpoint,
                no state. A visitor typed an address, pressed the arrow, saw the
                form sit there, and the address went nowhere. A capture that
                silently discards addresses is worse than no capture, because it
                loses the lead and tells the visitor we are not paying attention.
                Removed until there is something to send and somewhere to store
                it. A working mailto is directly below. */}
            <div>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="inline-flex items-center gap-2 border border-[#222] px-4 py-3 text-sm text-[#aaa] hover:text-[#80edfa] hover:border-[#80edfa] transition-colors font-sans"
              >
                {CONTACT_EMAIL}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M2 8H14M9 3L14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="lg:flex-1 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {footerLinks?.map((col) => (
              <div key={col?.heading}>
                <p className="font-mono-brand text-[0.65rem] text-[#666] uppercase tracking-widest mb-4">
                  {col?.heading}
                </p>
                <ul className="flex flex-col gap-2">
                  {col?.links?.map((link) => (
                    <li key={link?.label}>
                      <Link
                        href={link?.href}
                        className="text-sm text-[#aaa] hover:text-[#f5f5f8] transition-colors duration-200 font-sans"
                      >
                        {link?.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="lg:w-auto flex items-start">
            <Link href="/contact" className="btn-primary whitespace-nowrap">
              Book a call
            </Link>
          </div>
        </div>
      </div>

      {/* Middle: Compliance + Social */}
      <div className="border-t border-[#222]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          {/* Compliance badges */}
          <div className="flex items-center gap-4">
            {complianceBadges?.map((badge) => (
              <div
                key={badge?.label}
                className="compliance-badge"
              >
                {badge?.label}
              </div>
            ))}
          </div>

          {/* Contact. These three icons previously linked to linkedin.com,
              twitter.com and github.com, which are the platforms' front doors
              rather than accounts, so they sent a warm visitor off the site for
              no benefit. Replaced with the one way to actually reach us.
              Re-add socials here when the accounts exist. */}
          <div className="flex items-center gap-6">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-mono-brand text-[0.7rem] text-[#aaa] hover:text-[#80edfa] transition-colors tracking-widest"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </div>

      {/* Bottom: Copyright */}
      <div className="border-t border-[#222]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-6">
            <p className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest">
              &copy; 2026 TechSonar. All rights reserved.
            </p>
          </div>
          <p className="font-mono-brand text-[0.6rem] text-[#333] uppercase tracking-widest">
            Designed with precision
          </p>
        </div>
      </div>
    </footer>
  );
}
